/**
 * Waveform + region annotator aid for one audio clip (lazy-mounted by
 * AudioTranscriber — only the clip you're working on gets a WaveSurfer instance,
 * so a 175-clip list stays light). Detected-speech spans (model_segments) are
 * painted green so you can SEE where speech sits in a mostly-silent 30s clip;
 * drag on the wave to select a slice and it loop-plays while you transcribe.
 *
 * This is purely an audition aid: the saved label is still the full-clip text in
 * AudioTranscriber's textarea. Nothing here writes to corrected_text.
 */
import { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin, { type Region } from 'wavesurfer.js/plugins/regions';
import { Loader2, Play, Pause, X } from 'lucide-react';
import type { AudioUtterance } from '@/api/datasets';
import { getRate, subscribeRate } from '@/lib/playbackRate';

const SPEECH = 'rgba(34,197,94,0.18)';   // detected-speech hint (green)
const SELECT = 'rgba(99,102,241,0.30)';  // user selection (indigo)

export default function WaveClip({ src, segments }: { src: string; segments: AudioUtterance[] }) {
  const elRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const userRegion = useRef<Region | null>(null);
  const hintIds = useRef<Set<string>>(new Set());
  const addingHints = useRef(false);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!elRef.current) return;
    const regions = RegionsPlugin.create();
    const ws = WaveSurfer.create({
      container: elRef.current,
      height: 56,
      waveColor: '#9aa4b2',
      progressColor: '#6366f1',
      cursorColor: '#6366f1',
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      url: src,
      dragToSeek: true,
      plugins: [regions],
    });
    wsRef.current = ws;

    ws.on('decode', () => {
      // Paint detected-speech spans (non-interactive, just a visual hint).
      addingHints.current = true;
      for (const s of segments) {
        if (s.end > s.start) {
          const r = regions.addRegion({ start: s.start, end: s.end, color: SPEECH, drag: false, resize: false });
          hintIds.current.add(r.id);
        }
      }
      addingHints.current = false;
    });
    ws.on('ready', () => { setReady(true); ws.setPlaybackRate(getRate()); regions.enableDragSelection({ color: SELECT }); });
    const offRate = subscribeRate(() => ws.setPlaybackRate(getRate()));
    ws.on('play', () => setPlaying(true));
    ws.on('pause', () => setPlaying(false));
    ws.on('finish', () => setPlaying(false));

    regions.on('region-created', (region) => {
      if (addingHints.current) return;                 // a speech hint, not a selection
      if (userRegion.current && userRegion.current.id !== region.id) userRegion.current.remove();
      userRegion.current = region;
      region.play();                                   // start playing the new selection
    });
    regions.on('region-out', (region) => {
      if (region.id === userRegion.current?.id) region.play();   // loop the selection
    });
    regions.on('region-clicked', (region, e) => {
      e.stopPropagation();
      if (hintIds.current.has(region.id)) { region.play(true); return; }  // play a hint once
      userRegion.current = region;
      region.play();
    });

    return () => { offRate(); ws.destroy(); wsRef.current = null; userRegion.current = null; hintIds.current.clear(); };
    // src is pinned per clip and segments are stable for a clip → mount once.
  }, [src]);

  const toggle = () => {
    const ws = wsRef.current;
    if (!ws) return;
    if (ws.isPlaying()) { ws.pause(); return; }
    const r = userRegion.current;
    if (r) r.play(); else ws.play();
  };
  const clearSel = () => { userRegion.current?.remove(); userRegion.current = null; };

  return (
    <div className="flex flex-col gap-1">
      <div ref={elRef} className="w-full rounded bg-muted/30" />
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <button type="button" onClick={toggle} disabled={!ready}
          className="inline-flex items-center gap-1 rounded border px-2 py-0.5 hover:bg-muted disabled:opacity-50">
          {!ready ? <Loader2 className="size-3 animate-spin" /> : playing ? <Pause className="size-3" /> : <Play className="size-3" />}
          {playing ? 'Пауза' : 'Слушать'}
        </button>
        <button type="button" onClick={clearSel}
          className="inline-flex items-center gap-1 rounded border px-2 py-0.5 hover:bg-muted">
          <X className="size-3" /> участок
        </button>
        <span className="text-muted-foreground">зелёным — где найдена речь · выдели мышью участок, чтобы слушать по кругу</span>
      </div>
    </div>
  );
}
